import React, { useState } from "react";
import * as Yup from "yup";
import Checkbox from "../../../../../../../../design/1/js/templates/atoms/form-fields/checkbox";
import FormButton, {
    FormButtonVariation
} from "../../../../../../../../design/1/js/templates/atoms/form-fields/form-button";
import { IconName } from "../../../../../../../../design/1/js/templates/atoms/icon";
import TextField from "../../../../../../../../design/1/js/templates/molecules/text-field";
import "./index.scss";
import FormWithAnswerText from "../../../../../../../../design/1/js/templates/organisms/form-with-answer-text";
import StatusMessageVariation
    from "../../../../../../../../design/1/js/templates/molecules/status-message/lib/StatusMessageVariation";
import JsonResponse from "../../../../../../../../design/1/js/lib/entity/response/JsonResponse";
import RichContext from "../../../../../../../../design/1/js/lib/entity/response/RichContext";
import ErrorCircle from "../../../../../../../../design/1/js/templates/atoms/icon/icons/ErrorCircle";
import Modal from "../../../../../../../../design/1/js/templates/molecules/modal";

interface OnboardingFormProps {
    actionUrl: string;
    agbText: string;
    handleSubmit: (status: number) => void,
}

const OnboardingForm = ({
    actionUrl, agbText, handleSubmit,
}:OnboardingFormProps) => {
    const baseClassName = 'w-onboardingForm';

    const [isSystemErrorModalOpen, setSystemErrorModalOpen] = useState(false);
    const [systemErrorTraceId, setSystemErrorTraceId] = useState('');
    const [showAnswerText, setShowAnswerText] = useState(false);
    const [invalidCredentials, setInvalidCredentials] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setLoading] = useState(false);
    const int32MaxPositiveValue = 2147483647;

    const validationSchema = Yup.object().shape({
        id: Yup.number()
            .test('integer_check', window.sv_resource.get('form_errormsg_integer'), value => !value?.toString()?.includes('.'))
            .min(0, window.sv_resource.get('form_errormsg_integer'))
            .max(int32MaxPositiveValue, window.sv_resource.get('plf_onboarding_error_provide_id_message'))
            .required(window.sv_resource.get('form_errormsg_integer'))
            .typeError(window.sv_resource.get('form_errormsg_integer')),
        activationcode: Yup.string()
            .trim()
            .required(window.sv_resource.get('form_errormsg_string')),
        acceptedTerms: Yup.boolean()
            .oneOf([true], window.sv_resource.get('form_errormsg_terms'))
            .required(window.sv_resource.get('form_errormsg_terms')),
    });

    const initialValues = Object.freeze({
        id: null as number,
        activationcode: '',
        acceptedTerms: false,
    });

    const formHandleSubmit = (status: number, jsonResponse: JsonResponse<RichContext>) => {
        setLoading(false);
        switch (jsonResponse.statusCode) {
            case 200:
                setShowAnswerText(true);
                handleSubmit(status);
                break;
            // Form Error
            case 400:
            case 401:
                setShowAnswerText(false);
                setInvalidCredentials(true);
                setErrorMessage(jsonResponse.context.description);
                break;
            // Page Error
            case 403:
                setShowAnswerText(true);
                setErrorMessage('');
                handleSubmit(status);
                break;
            // Modal Error
            default:
                setShowAnswerText(false);
                setSystemErrorTraceId(jsonResponse.traceId);
                setSystemErrorModalOpen(true);
        }
    }

    return (
        <FormWithAnswerText
            actionUrl={actionUrl}
            className={`${baseClassName}`}
            handleSubmit={formHandleSubmit}
            beforeSubmit={() => {
                setLoading(true);
            }}
            handleValidate={validationSchema}
            initialValues={initialValues}
            variation={StatusMessageVariation.Standard}
            name="Onboarding"
            showAnswerText={showAnswerText}
        >
            {formik => (
                <div className={`${baseClassName}__inner p-4 col-12 col-sm-9 col-lg-7 mx-auto`}>
                    <div className="row gap-lg-s">
                        { errorMessage ?
                            <span className={`error-message d-flex align-items-center`}>
                                <ErrorCircle/>
                                <span className={`ms-1`}>{errorMessage}</span>
                            </span>
                            : <></>
                        }
                        <div className={`${baseClassName}__form col-12`}>
                            <TextField
                                className={`mb-4`}
                                id="id"
                                label={window.sv_resource.get('plf_onboarding_form_id_label')}
                                name="id"
                                type="number"
                                touched={formik.touched.id}
                                errors={formik.errors.id ? formik.errors.id : invalidCredentials}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                disabled={isLoading}
                            />
                            <TextField
                                className={`mb-4`}
                                id="activationcode"
                                label={window.sv_resource.get('plf_onboarding_form_activationcode_label')}
                                name="activationcode"
                                type="text"
                                autoComplete={`off`}
                                touched={formik.touched.activationcode}
                                errors={formik.errors.activationcode ? formik.errors.activationcode : invalidCredentials}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                disabled={isLoading}
                            />
                            <Checkbox
                                className={`${baseClassName}__checkbox`}
                                errors={formik.errors.acceptedTerms}
                                touched={formik.touched.acceptedTerms}
                                id="acceptedTerms"
                                name="acceptedTerms"
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                label={agbText}
                                disabled={isLoading}
                            />
                            <FormButton
                                className={`${baseClassName}__formButton mt-4`}
                                disabled={Object.keys(formik.errors).length > 0 || Object.keys(formik.touched).length === 0 || isLoading}
                                text={window.sv_resource.get('plf_onboarding_submit_btn')}
                                type="submit"
                                variation={FormButtonVariation.Primary}
                                isLoading={isLoading}
                                hasIcon={false}
                            />
                        </div>
                    </div>
                    <Modal icon={IconName.ErrorCircle} className={`onboarding-system-error-modal`} titleTag={`h2`} isOpen={isSystemErrorModalOpen} setIsOpen={setSystemErrorModalOpen} title={window.sv_resource.get('plf_onboarding_error_popup_title')}>
                        {window.sv_resource.get('plf_onboarding_error_popup_text')}
                        <br/>
                        <span className="small-text">
                            ({window.sv_resource.get('plf_error_reference_id')}:{' '}{systemErrorTraceId})
                        </span>
                    </Modal>
                </div>
            )}
        </FormWithAnswerText>
    );
};

export default OnboardingForm;
